import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { useCallback, useEffect, useRef, useState } from 'react'
import type React from 'react'

export type SubstitutionDragState = {
	sourceIndex: number
	width: number
	height: number
	label: string
	positionName: string
	isPlayerLabel: boolean
	isTouch: boolean
	liftY: number
}

type SubstitutionReturnGhost = {
	left: number
	top: number
	width: number
	height: number
	deltaX: number
	deltaY: number
	label: string
	positionName: string
	isPlayerLabel: boolean
}

type PendingSubstitutionGesture = {
	pointerId: number
	armed: boolean
	timer: ReturnType<typeof setTimeout>
	cleanup: () => void
}

type SubstitutionDragPosition = {
	left: number
	top: number
}

type UseSubstitutionDragProps = {
	roster: RosterData
	endRoster: RosterData
	editMode: boolean
	showAltBoard: boolean
	numPeriods: NumPeriods
	fieldPositions: readonly FieldPosition[]
	playerNames: Record<PlayerId, string> | null
	setAmDragging: (value: boolean) => void
	swapEndPositions: (from: RosterPositionIndex, to: RosterPositionIndex) => void
	onLongPressActivated?: () => void
	onLongPressChanged?: () => void
}

export type SubstitutionDragController = {
	dragState: SubstitutionDragState | null
	dragPosition: React.MutableRefObject<SubstitutionDragPosition>
	dragGhostRef: React.RefObject<HTMLDivElement>
	returnGhost: SubstitutionReturnGhost | null
	recentIndexes: number[]
	pendingReturnIndex: number | null
	gentleSettleIndex: number | null
	onPointerDown: (
		i: I,
		event: React.PointerEvent<HTMLDivElement>,
		label: string,
		positionName: string,
	) => void
	onNormalDragStart: () => void
	onNormalDragEnd: () => void
	isNormalDropSuppressed: () => boolean
}

const LONG_PRESS_MS = 615
const MOVE_TOLERANCE = 9
const TOUCH_ACTIVATION_LIFT = 24
const TOUCH_DRAG_LIFT = 6
const TOUCH_DRAG_START_DISTANCE = 6

export function useSubstitutionDrag(props: UseSubstitutionDragProps): SubstitutionDragController {
	const {
		roster,
		endRoster,
		editMode,
		showAltBoard,
		numPeriods,
		fieldPositions,
		playerNames,
		setAmDragging,
		swapEndPositions,
		onLongPressActivated,
		onLongPressChanged,
	} = props
	const [ dragState, setDragState ] = useState<SubstitutionDragState | null>(null)
	const [ returnGhost, setReturnGhost ] = useState<SubstitutionReturnGhost | null>(null)
	const [ recentIndexes, setRecentIndexes ] = useState<number[]>([ ])
	const [ pendingReturnIndex, setPendingReturnIndex ] = useState<number | null>(null)
	const [ gentleSettleIndex, setGentleSettleIndex ] = useState<number | null>(null)
	const pendingGesture = useRef<PendingSubstitutionGesture | null>(null)
	const dragPosition = useRef<SubstitutionDragPosition>({ left: 0, top: 0 })
	const dragGhostRef = useRef<HTMLDivElement>(null)
	const dragPositionFrame = useRef<number | null>(null)
	const recentTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const returnGhostTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const normalDragActive = useRef(false)
	const pendingReturnCompletion = useRef<(() => void) | null>(null)
	const suppressNormalDropUntil = useRef(0)

	useEffect(() => () => {
		pendingGesture.current?.cleanup()
		if (recentTimer.current) {
			clearTimeout(recentTimer.current)
		}
		if (returnGhostTimer.current) {
			clearTimeout(returnGhostTimer.current)
		}
		if (dragPositionFrame.current !== null) {
			cancelAnimationFrame(dragPositionFrame.current)
		}
		pendingReturnCompletion.current = null
	}, [ ])

	const onNormalDragStart = useCallback(() => {
		normalDragActive.current = true
	}, [ ])

	const onNormalDragEnd = useCallback(() => {
		normalDragActive.current = false
		const completeReturn = pendingReturnCompletion.current
		pendingReturnCompletion.current = null
		completeReturn?.()
	}, [ ])

	const updateGhostPosition = useCallback((left: number, top: number) => {
		dragPosition.current = { left, top }
		if (dragPositionFrame.current !== null) {
			return
		}

		dragPositionFrame.current = requestAnimationFrame(() => {
			dragPositionFrame.current = null
			const position = dragPosition.current
			if (dragGhostRef.current) {
				dragGhostRef.current.style.transform = `translate3d(${position.left}px, ${position.top}px, 0)`
			}
		})
	}, [ ])

	const queryTarget = useCallback((x: number, y: number, sourceIndex: number) => {
		const playersPerPeriod = roster.length / numPeriods
		const sourcePeriod = Math.floor(sourceIndex / playersPerPeriod)

		for (const element of document.elementsFromPoint(x, y)) {
			const tile = element instanceof HTMLElement ? element.closest<HTMLElement>('.cell-position[data-i]') : null
			if (!tile || !tile.closest('.ion-page:not(.ion-page-hidden)')) {
				continue
			}

			const targetIndex = Number(tile.dataset.endRosterIndex ?? tile.dataset.i)
			if (
				Number.isInteger(targetIndex) &&
				targetIndex !== sourceIndex &&
				Math.floor(targetIndex / playersPerPeriod) === sourcePeriod
			) {
				return {
					index: targetIndex,
					tile,
				}
			}
		}

		return null
	}, [ roster.length, numPeriods ])

	const onPointerDown = useCallback((
		i: I,
		event: React.PointerEvent<HTMLDivElement>,
		label: string,
		positionName: string,
	) => {
		if (!editMode || typeof i !== 'number' || event.button !== 0) {
			return
		}

		pendingGesture.current?.cleanup()

		const sourceIndex = i
		const pointerId = event.pointerId
		const isTouch = event.pointerType === 'touch'
		const startX = event.clientX
		const startY = event.clientY
		const bounds = event.currentTarget.getBoundingClientRect()
		const sourceCenterX = bounds.left + bounds.width / 2
		const sourceCenterY = bounds.top + bounds.height / 2
		const pointerOffsetX = startX - sourceCenterX
		const pointerOffsetY = startY - sourceCenterY
		const activationLiftY = isTouch ? Math.min(TOUCH_ACTIVATION_LIFT, Math.max(0, bounds.top - 8)) : 4
		const horizontalDragBound = bounds.width * 0.4
		let targetIndex: number | null = null
		let targetBounds: DOMRect | null = null
		let targetTile: HTMLElement | null = null
		let selectionStarted = false
		let touchDragStarted = false

		const endSelection = () => {
			if (selectionStarted) {
				selectionStarted = false
				void Haptics.selectionEnd()
			}
		}

		const cleanup = () => {
			endSelection()
			targetTile?.classList.remove('cell-substitution-target')
			targetTile = null
			const pending = pendingGesture.current
			if (pending) {
				clearTimeout(pending.timer)
			}
			document.removeEventListener('pointermove', onPointerMove, true)
			document.removeEventListener('pointerup', onPointerUp, true)
			document.removeEventListener('pointercancel', onPointerCancel, true)
			pendingGesture.current = null
		}

		const finish = (pointerEvent: PointerEvent, cancelled: boolean) => {
			const pending = pendingGesture.current
			if (!pending || pointerEvent.pointerId !== pointerId) {
				return
			}

			if (pending.armed) {
				pointerEvent.preventDefault()
				pointerEvent.stopImmediatePropagation()
				suppressNormalDropUntil.current = Date.now() + 500
				setAmDragging(false)

				if (
					!cancelled &&
					targetIndex !== null &&
					endRoster[sourceIndex] !== endRoster[targetIndex]
				) {
					const playersPerPeriod = roster.length / numPeriods
					const periodStart = Math.floor(targetIndex / playersPerPeriod) * playersPerPeriod
					const displacedPlayer = endRoster[targetIndex]
					const displacedPlayerStartIndex = roster.findIndex((playerId, index) => (
						index >= periodStart && index < periodStart + playersPerPeriod && playerId === displacedPlayer
					))
					const displacedPlayerStartPosition = fieldPositions[displacedPlayerStartIndex % playersPerPeriod] || '-'
					const destinationVisualIndex = showAltBoard ? targetIndex : sourceIndex
					const sourceVisualIndex = showAltBoard ? sourceIndex : targetIndex
					void swapEndPositions(sourceIndex, targetIndex)
					onLongPressChanged?.()
					setRecentIndexes([ destinationVisualIndex ])
					setGentleSettleIndex(null)
					if (targetBounds) {
						setPendingReturnIndex(sourceVisualIndex)
						setReturnGhost({
							left: targetBounds.left,
							top: targetBounds.top,
							width: targetBounds.width,
							height: targetBounds.height,
							deltaX: bounds.left - targetBounds.left,
							deltaY: bounds.top - targetBounds.top,
							label: showAltBoard ? (playerNames?.[endRoster[targetIndex]] || endRoster[targetIndex]) : (fieldPositions[targetIndex % playersPerPeriod] || '-'),
							positionName: showAltBoard ? displacedPlayerStartPosition : (fieldPositions[targetIndex % playersPerPeriod] || '-'),
							isPlayerLabel: showAltBoard,
						})
						if (returnGhostTimer.current) {
							clearTimeout(returnGhostTimer.current)
						}
						returnGhostTimer.current = setTimeout(() => {
							const completeReturn = () => {
								setReturnGhost(null)
								setPendingReturnIndex(null)
								setRecentIndexes([ destinationVisualIndex, sourceVisualIndex ])
								setGentleSettleIndex(sourceVisualIndex)
								if (recentTimer.current) {
									clearTimeout(recentTimer.current)
								}
								recentTimer.current = setTimeout(() => {
									setRecentIndexes([ ])
									setGentleSettleIndex(null)
								}, 550)
							}
							if (normalDragActive.current) {
								pendingReturnCompletion.current = completeReturn
							} else {
								completeReturn()
							}
						}, 440)
					} else {
						setRecentIndexes([ sourceIndex, targetIndex ])
						if (recentTimer.current) {
							clearTimeout(recentTimer.current)
						}
						recentTimer.current = setTimeout(() => setRecentIndexes([ ]), 550)
					}
					Haptics.impact({ style: ImpactStyle.Light })
				}

				setDragState(null)
			}

			cleanup()
		}

		const onPointerMove = (pointerEvent: PointerEvent) => {
			const pending = pendingGesture.current
			if (!pending || pointerEvent.pointerId !== pointerId) {
				return
			}

			if (!pending.armed) {
				const distance = Math.hypot(pointerEvent.clientX - startX, pointerEvent.clientY - startY)
				if (distance > MOVE_TOLERANCE) {
					cleanup()
				}
				return
			}

			pointerEvent.preventDefault()
			pointerEvent.stopImmediatePropagation()
			const distance = Math.hypot(pointerEvent.clientX - startX, pointerEvent.clientY - startY)
			if (isTouch && !touchDragStarted && distance > TOUCH_DRAG_START_DISTANCE) {
				touchDragStarted = true
				dragGhostRef.current?.style.setProperty('--substitution-ghost-lift', `${-TOUCH_DRAG_LIFT}px`)
			}
			const desiredCenterX = pointerEvent.clientX - pointerOffsetX
			const constrainedCenterX = Math.max(
				sourceCenterX - horizontalDragBound,
				Math.min(sourceCenterX + horizontalDragBound, desiredCenterX),
			)
			const centerY = pointerEvent.clientY - pointerOffsetY
			const target = queryTarget(constrainedCenterX, centerY, sourceIndex)
			const nextTargetIndex = target?.index ?? null
			const targetChanged = nextTargetIndex !== targetIndex
			if (targetChanged) {
				targetTile?.classList.remove('cell-substitution-target')
				targetTile = target?.tile ?? null
				targetTile?.classList.add('cell-substitution-target')
				targetIndex = nextTargetIndex
				targetBounds = targetTile?.getBoundingClientRect() ?? null
				if (nextTargetIndex !== null) {
					void Haptics.selectionChanged()
				}
			}
			updateGhostPosition(constrainedCenterX - bounds.width / 2, centerY - bounds.height / 2)
		}

		const onPointerUp = (pointerEvent: PointerEvent) => finish(pointerEvent, false)
		const onPointerCancel = (pointerEvent: PointerEvent) => finish(pointerEvent, true)

		const timer = setTimeout(() => {
			const pending = pendingGesture.current
			if (!pending || pending.pointerId !== pointerId) {
				return
			}

			pending.armed = true
			onLongPressActivated?.()
			normalDragActive.current = false
			const completeReturn = pendingReturnCompletion.current
			pendingReturnCompletion.current = null
			completeReturn?.()
			selectionStarted = true
			void Haptics.selectionStart()
			suppressNormalDropUntil.current = Date.now() + 1000
			setAmDragging(true)
			updateGhostPosition(bounds.left, bounds.top)
			setDragState({
				sourceIndex,
				width: bounds.width,
				height: bounds.height,
				label,
				positionName,
				isPlayerLabel: showAltBoard,
				isTouch,
				liftY: activationLiftY,
			})
			Haptics.impact({ style: ImpactStyle.Medium })
		}, LONG_PRESS_MS)

		pendingGesture.current = {
			pointerId,
			armed: false,
			timer,
			cleanup,
		}

		document.addEventListener('pointermove', onPointerMove, { capture: true, passive: false })
		document.addEventListener('pointerup', onPointerUp, true)
		document.addEventListener('pointercancel', onPointerCancel, true)
	}, [ editMode, endRoster, fieldPositions, numPeriods, onLongPressActivated, onLongPressChanged, playerNames, queryTarget, roster, setAmDragging, showAltBoard, swapEndPositions, updateGhostPosition ])

	const isNormalDropSuppressed = useCallback(() => Date.now() < suppressNormalDropUntil.current, [ ])

	return {
		dragState,
		dragPosition,
		dragGhostRef,
		returnGhost,
		recentIndexes,
		pendingReturnIndex,
		gentleSettleIndex,
		onPointerDown,
		onNormalDragStart,
		onNormalDragEnd,
		isNormalDropSuppressed,
	}
}
